import { type ServerMessage, normalizeRoomCode } from '@euchre/core';
import { h, replace } from './dom.js';
import { Net, storedName } from './net.js';
import { IS_PREVIEW, app, rerender, rerenderAt, send, toast, wire } from './state.js';
import { homeScreen } from './ui/home.js';
import { lobbyScreen } from './ui/lobby.js';
import { resetSidebar, sidebar } from './ui/sidebar.js';
import { seatCovered, stillThereAsked, tableScreen } from './ui/table.js';
import { applyLargePrint, chime, closeFeedback, feedback, feedbackDialog, feedbackFailed, feedbackSaved } from './ui/extras.js';

const root = document.getElementById('app')!;
app.name = storedName();

// A share link looks like /r/ABCDE.
const linkMatch = /^\/r\/([A-Za-z0-9]{5})\/?$/.exec(location.pathname);
if (linkMatch) app.pendingJoin = normalizeRoomCode(linkMatch[1]!);

function onMessage(msg: ServerMessage): void {
  switch (msg.type) {
    case 'welcome':
      app.playerId = msg.playerId;
      if (!app.name) app.name = msg.name === 'Player' ? '' : msg.name;
      if (app.pendingJoin && app.name) {
        send({ type: 'join_room', code: app.pendingJoin });
        app.pendingJoin = null;
      }
      break;
    case 'room': {
      const prev = app.room;
      app.room = msg.room;
      app.clockOffset = msg.room.serverTime - Date.now();
      // Chat history arrives with each snapshot and live via 'chat'; merge both.
      app.chat = prev?.code === msg.room.code ? [...app.chat, ...msg.room.chat] : [...msg.room.chat];
      app.chat.sort((a, b) => a.id - b.id);
      dedupeChat();
      noteTrick();
      if (prev?.code !== msg.room.code) history.replaceState(null, '', `/r/${msg.room.code}`);
      break;
    }
    case 'left_room':
      app.room = null;
      app.ui.drawerOpen = false;
      app.ui.menuOpen = false;
      app.chat = [];
      app.heldTrick = null;
      resetSidebar();
      history.replaceState(null, '', '/');
      break;
    case 'chat':
      if (!app.chat.some((c) => c.id === msg.entry.id)) {
        app.chat.push(msg.entry);
        const heckle = msg.entry.tone === 'vegas' && app.ui.dealerTalk === 'vegas';
        if (!chatVisible() && (msg.entry.from || heckle)) app.unreadChat++;
        if (heckle) app.lastHeckle = { text: msg.entry.text, deal: app.room?.game?.dealNumber ?? -1 };
      }
      break;
    case 'emote': {
      const until = Date.now() + 2600;
      app.emotes = [...app.emotes.filter((e) => e.until > Date.now()), { id: Math.random(), seat: msg.seat, name: msg.name, emote: msg.emote, until }];
      rerenderAt(until);
      break;
    }
    case 'error':
      if (feedback.sending) feedbackFailed();
      if (msg.code === 'superseded') {
        // The banner explains this and offers a button to play here instead.
      } else {
        toast(msg.message);
      }
      // A share link to a room that no longer exists: fall back to the home screen.
      if (/No room/.test(msg.message)) history.replaceState(null, '', '/');
      break;
    case 'feedback_ok':
      feedbackSaved();
      toast('Thanks! Your note was sent.');
      break;
    case 'pong':
      return;
  }
  rerender();
}

/** Whether the chat is on screen right now (in the lobby and at the table it lives in a panel that may be closed). */
function chatVisible(): boolean {
  return app.ui.tab === 'chat' && app.ui.drawerOpen;
}

// Escape closes the table's side panel or menu.
document.addEventListener('keydown', (ev) => {
  if (ev.key !== 'Escape') return;
  if (feedback.open) return closeFeedback();
  if (app.ui.menuOpen || app.ui.drawerOpen) {
    app.ui.menuOpen = false;
    app.ui.drawerOpen = false;
    rerender();
  }
});

function dedupeChat(): void {
  const seen = new Set<number>();
  app.chat = app.chat.filter((c) => (seen.has(c.id) ? false : (seen.add(c.id), true)));
  if (app.chat.length > 150) app.chat = app.chat.slice(-150);
}

/** Keep a just-finished trick visible for a moment. */
function noteTrick(): void {
  const g = app.room?.game;
  if (!g) return;
  const last = g.tricks.at(-1);
  const key = last ? `${g.dealNumber}:${last.number}` : '';
  if (key && key !== app.lastSeenTrickKey && app.lastSeenTrickKey !== '') {
    // A hand's last trick stays up 3 seconds (the server waits too) before the hand summary; others 2 seconds,
    // matching the server's pause after a trick.
    const lastOfHand = g.phase === 'hand_complete' || g.phase === 'game_over';
    app.heldTrick = { trick: last!, until: Date.now() + (lastOfHand ? 3000 : 2000) };
    rerenderAt(app.heldTrick.until);
  }
  if (!key && g.tricks.length === 0) app.heldTrick = null;
  app.lastSeenTrickKey = key || `${g.dealNumber}:0`;
}

const net = new Net(
  onMessage,
  (status) => {
    app.conn = status;
    rerender();
  },
  () => app.name,
);
wire((msg) => net.send(msg), render);

let lastScreen = '';
const shell = h('div', { class: 'shell' });
const banner = h('div', { class: 'conn-banner', attrs: { role: 'status' } });
const toastEl = h('div', { class: 'toast', attrs: { role: 'alert' } });
const feedbackHost = h('div', { class: 'feedback-host' });
root.replaceChildren(banner, shell, feedbackHost, toastEl);
applyLargePrint();

// Chimes: when your turn starts, and when you're asked "Are you still there?"
let wasMyTurn = false;
let wasAsked = false;
function chimes(myTurn: boolean, asked: boolean): void {
  if (asked && !wasAsked) chime('stillThere');
  else if (myTurn && !wasMyTurn) chime('turn');
  wasMyTurn = myTurn;
  wasAsked = asked;
}

function render(): void {
  const room = app.room;
  const screen = !room ? 'home' : room.game && room.status !== 'lobby' ? 'table' : 'lobby';
  document.body.dataset.screen = screen;
  document.body.dataset.drawer = screen !== 'home' && app.ui.drawerOpen ? 'open' : 'closed';
  // Only animate the panel once we're already in a room, so it doesn't slide past when you arrive.
  document.body.dataset.drawerAnim = screen !== 'home' && lastScreen !== 'home' && lastScreen !== '' ? 'on' : 'off';
  if (screen === 'home') {
    if (lastScreen !== 'home') replace(shell, homeScreen());
    else homeScreen();
  } else {
    const main = screen === 'table' ? tableScreen(room!) : lobbyScreen(room!);
    const side = sidebar();
    if (lastScreen === 'home' || lastScreen === '' || shell.firstElementChild?.classList.contains('home')) {
      replace(shell, main, side);
    } else {
      shell.firstElementChild!.replaceWith(main);
      if (side.parentElement !== shell) shell.append(side);
    }
  }
  lastScreen = screen;

  banner.hidden = app.conn === 'open';
  if (app.conn === 'superseded') {
    replace(
      banner,
      'You opened the game in another tab or device, so this one is paused. ',
      h('button', { class: 'btn small', type: 'button', text: 'Play here instead', onclick: () => net.resume() }),
    );
  } else {
    banner.textContent = app.conn === 'connecting' ? 'Connecting…' : 'Connection lost. Reconnecting — your seat is held for you.';
  }

  const t = app.toast;
  if (t && t.until > Date.now()) {
    toastEl.textContent = t.text;
    toastEl.hidden = false;
  } else {
    toastEl.hidden = true;
  }
  // Show "Your turn" in the browser tab too, for anyone who switched to another tab.
  const g = room?.game;
  const myTurn = !!g && room!.you.seat !== null && g.turn === room!.you.seat && g.legalActions.length > 0;
  const site = IS_PREVIEW ? "Dave's Euchre (Preview)" : "Dave's Euchre";
  const flag = !room ? '' : stillThereAsked(room) ? '⚠ Are you still there? · ' : seatCovered(room) ? '⚠ A bot is playing for you · ' : myTurn ? '▶ Your turn · ' : '';
  document.title = room ? `${flag}${site} · ${room.code}` : site;
  chimes(myTurn && !seatCovered(room!), !!room && stillThereAsked(room));

  const dialog = feedbackDialog();
  if (!dialog) feedbackHost.replaceChildren();
  else if (dialog.parentElement !== feedbackHost) feedbackHost.replaceChildren(dialog);
}

render();
net.connect();
